import { useCallback, useEffect, useRef, useState } from 'react';
import { Auth } from "aws-amplify";

export type FetchHeader = {
    key: string;
    value: string;
};

const useFetch = (url: string, headers?: FetchHeader[]) => {

    const [isLoading, setIsLoading] = useState(false);
    const [isTokenError, setIsTokenError] = useState(false);
    const [data, setData] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);
    const token = useRef('');

    const fetchData = useCallback(async (token: string, headers?: FetchHeader[]) => {
        try {
            headers = headers || [];

            headers.push({ key: 'Authorization', value: `Bearer ${token}` });
            headers.push({ key: 'Content-Type', value: 'application/json' });

            const response = await fetch(url, {
                headers: {
                    ...Object.fromEntries(headers.map((header) => [header.key, header.value]))
                },
                method: 'GET'
            });

            // Interceptar 401/403 para refresh automático
            if (response.status === 401 || response.status === 403) {
                console.log('Token expired, attempting refresh...');
                try {
                    // Tentar obter novo token
                    const session = await Auth.currentSession();
                    const newToken = session.getIdToken().getJwtToken();
                    
                    // Atualizar localStorage
                    localStorage.setItem('authToken', newToken);
                    
                    // Retry request com novo token
                    const retryHeaders = headers.map(h => 
                        h.key === 'Authorization' ? { key: 'Authorization', value: `Bearer ${newToken}` } : h
                    );
                    
                    const retryResponse = await fetch(url, {
                        headers: {
                            ...Object.fromEntries(retryHeaders.map((header) => [header.key, header.value]))
                        },
                        method: 'GET'
                    });
                    
                    if (!retryResponse.ok) {
                        throw new Error(`HTTP error after retry! Status: ${retryResponse.status}`);
                    }
                    
                    const data = await retryResponse.json();
                    setData(data);
                    setIsLoading(false);
                    return;
                    
                } catch (refreshError) {
                    console.log('Token refresh failed:', refreshError);
                    setIsTokenError(true);
                    localStorage.removeItem('authToken');
                    throw new Error('Authentication failed - please login again');
                }
            }

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            setData(data);
            setIsLoading(false);
        } catch (error: unknown) {
            if (error instanceof Error) {
                setError(error.message);
            } else {
                setError(String(error));
            }
            setIsLoading(false);
        }
    }, [url]);

    useEffect(() => {
        let isMounted = true;
        setIsLoading(true);
        
        const initializeFetch = async () => {
            try {
                const session = await Auth.currentSession();
                const tkn = session.getIdToken().getJwtToken();
                
                if (isMounted) {
                    token.current = tkn;
                    // Atualizar localStorage com token atual
                    localStorage.setItem('authToken', tkn);
                    await fetchData(tkn, headers);
                }
            } catch (error) {
                console.log('Session error:', error);
                if (isMounted) {
                    // Distinguir entre token expirado vs outros erros
                    if (error instanceof Error && error.message.includes('No current user')) {
                        setIsTokenError(true);
                        localStorage.removeItem('authToken');
                    }
                    setError(error instanceof Error ? error.message : String(error));
                    setIsLoading(false);
                }
            }
        };
        
        initializeFetch();

        return () => {
            isMounted = false;
        };
    }, [url, fetchData]); // Remove headers da dependência

    return { isLoading, data, error, isTokenError };
};

export default useFetch;